import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { NoopT } from 'declarations/types/common';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useCloseTicket } from 'hooks/api/useTickets';

type UseTicketClosePopupT = {
  isClosingTicket: boolean;
  isTicketSuccessfullyClosed: boolean;
  isTicketClosingError: boolean;
  closeTicketErrorMessage: string | undefined;
  closeTicketValidationErrorMessage: string | undefined;
  showPopupContent: boolean;
  handleCloseTicket: (ticketIds: string[]) => NoopT;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
};

const useTicketClosePopup = (): UseTicketClosePopupT => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();

  /**
   * Api hooks
   */
  const closeTicketMutation = useCloseTicket({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
    },
  });

  /**
   * Variables
   */
  const closeTicketErrorMessage = useMemo(
    () => getErrorMessage(closeTicketMutation.error),
    [closeTicketMutation.error]
  );
  const closeTicketValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(closeTicketMutation.error),
    [closeTicketMutation.error]
  );

  /**
   * Handlers
   */
  const resetCloseTicketMutationState = useCallback(() => {
    if (closeTicketMutation.isSuccess || closeTicketMutation.isError) {
      closeTicketMutation.reset();
    }
  }, [closeTicketMutation]);
  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => () => {
      onClosePopupCb();
      resetCloseTicketMutationState();
    },
    [resetCloseTicketMutationState]
  );
  const handleCloseTicket = useCallback(
    (ticketIds: string[]) => () => {
      closeTicketMutation.mutate({ ticketIds });
    },
    [closeTicketMutation]
  );

  return {
    isClosingTicket: closeTicketMutation.isLoading,
    isTicketSuccessfullyClosed: closeTicketMutation.isSuccess,
    isTicketClosingError: closeTicketMutation.isError,
    closeTicketErrorMessage,
    closeTicketValidationErrorMessage,
    showPopupContent: closeTicketMutation.isIdle || closeTicketMutation.isLoading,
    handleCloseTicket,
    handleClosePopup,
  };
};

export default useTicketClosePopup;
